import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Play, Star } from "lucide-react";
import { resolveMedia } from "@/lib/api";
import { WatchlistButton } from "@/components/WatchlistButton";

export const AnimeCard = ({ anime, index = 0 }) => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), { stiffness: 200, damping: 20 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-8, 8]), { stiffness: 200, damping: 20 });

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.6, delay: (index % 5) * 0.07, ease: [0.2, 0.8, 0.2, 1] }}
      style={{ perspective: 900 }}
    >
      <motion.div
        ref={ref}
        onMouseMove={onMove}
        onMouseLeave={reset}
        style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
        className="group relative"
      >
        <Link to={`/anime/${anime.id}`} data-testid={`anime-card-${anime.id}`} className="block">
          <div className="relative aspect-[3/4] overflow-hidden kp-card-clip bg-[#0F0D16] border border-white/[0.06] group-hover:border-[#FF2A55]/50 transition-colors duration-300">
            <img
              src={resolveMedia(anime.cover_url)}
              alt={anime.title}
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#08070C] via-[#08070C]/30 to-transparent" />
            <div className="absolute inset-0 kp-scanlines opacity-40" />
            <div className="absolute top-3 left-3 flex items-center gap-1.5 kp-glass px-2 py-1 text-[0.65rem] font-mono">
              <Star size={11} className="text-[#00F0FF] fill-[#00F0FF]" /> {anime.rating ?? "—"}
            </div>
            <div className="absolute top-3 right-3 flex items-center gap-2">
              <WatchlistButton anime={anime} />
              <span className="bg-[#FF2A55] px-2 py-1.5 text-[0.62rem] font-mono tracking-widest">{anime.episode_count} EP</span>
            </div>
            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
              <span className="w-14 h-14 rounded-full bg-[#FF2A55] flex items-center justify-center kp-glow-crimson scale-75 group-hover:scale-100 transition-transform duration-300">
                <Play size={20} className="fill-white text-white ml-0.5" />
              </span>
            </div>
            <div className="absolute bottom-0 inset-x-0 p-4" style={{ transform: "translateZ(30px)" }}>
              <div className="kp-eyebrow !text-[0.58rem] mb-1 truncate">{anime.genres?.slice(0, 2).join(" · ")}</div>
              <h3 className="font-display font-semibold text-sm sm:text-base leading-tight line-clamp-2">{anime.title}</h3>
            </div>
          </div>
        </Link>
      </motion.div>
    </motion.div>
  );
};
